import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Megaphone, MousePointerClick, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  ["financing", "Financing"], ["inspections", "Inspections"], ["cleaning", "Cleaning"], ["moving", "Moving"],
  ["contractors", "Contractors"], ["landscaping", "Landscaping"], ["insurance", "Insurance"], ["title", "Title Services"], ["other", "Other"],
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES);
const cardShadow = { boxShadow: "0 4px 20px rgba(15,23,42,0.035)" };
const EMPTY_FORM = { business_name: "", category: "financing", logo_url: "", image_url: "", headline: "", description: "", destination_url: "", button_label: "Visit Website", contact_email: "", contact_phone: "", service_area: "", disclosure_text: "", active_from: "", active_until: "", enabled: true };

export function AdvertisersTab() {
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const reload = () => api.get("/admin/advertisers").then((r) => setAdvertisers(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { reload(); }, []);

  const remove = async (advertiser) => {
    if (!window.confirm(`Remove "${advertiser.business_name}"? This also removes it from every property website it's assigned to.`)) return;
    try { await api.delete(`/admin/advertisers/${advertiser.id}`); toast.success("Advertiser removed"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const toggleEnabled = async (advertiser, enabled) => {
    try { await api.patch(`/admin/advertisers/${advertiser.id}`, { ...advertiser, enabled }); toast.success(enabled ? "Advertiser enabled" : "Advertiser disabled"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl space-y-7 fade-up" data-testid="advertisers-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div><span className="eyebrow">Sponsorships</span><h1 className="font-serif-display text-4xl sm:text-[42px] mt-2 text-[#101828]">Advertisers</h1><p className="text-sm text-[#475467] mt-3 max-w-2xl">Local businesses you can feature on property websites — financing, inspections, moving, and more. Assign them to specific listings from each project's Property Website tab.</p></div>
        <Button data-testid="advertiser-add-btn" onClick={() => setAdding(true)} className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white gap-2"><Plus size={16} />Add advertiser</Button>
      </header>

      <section className="rounded-[14px] border border-[#E4E9F0] bg-white overflow-hidden" style={cardShadow} data-testid="advertisers-list">
        {loading && <div className="p-10 text-center text-sm text-[#94A3B8]">Loading…</div>}
        {!loading && advertisers.length === 0 && <div data-testid="advertisers-empty" className="p-12 text-center text-sm text-[#94A3B8]"><Megaphone size={28} className="mx-auto mb-3 text-[#CBD5E1]" />No advertisers yet. Add your first local-service partner.</div>}
        {advertisers.map((ad) => (
          <div key={ad.id} data-testid={`advertiser-row-${ad.id}`} className="flex items-center gap-4 px-5 sm:px-6 py-4 border-b border-[#F1F2F4] last:border-b-0 hover:bg-[#F8FAFC]">
            {ad.logo_url ? <img src={ad.logo_url} alt="" className="h-10 w-10 rounded-lg object-contain border border-[#E4E9F0] shrink-0" /> : <div className="h-10 w-10 rounded-lg bg-[#F7F9FC] border border-[#E4E9F0] grid place-items-center text-[#94A3B8] shrink-0"><Megaphone size={16} /></div>}
            <div className="min-w-0 flex-1">
              <div className="font-medium text-[#101828] truncate">{ad.business_name}</div>
              <div className="text-xs text-[#475467] font-mono mt-0.5">{CATEGORY_LABEL[ad.category] || ad.category}{ad.service_area ? ` · ${ad.service_area}` : ""}</div>
            </div>
            <div data-testid={`advertiser-stats-${ad.id}`} className="hidden sm:flex items-center gap-4 text-xs text-[#475467] font-mono shrink-0">
              <span className="flex items-center gap-1.5" title="Views on property websites"><Eye size={13} className="text-[#94A3B8]" />{ad.view_count || 0} <span className="text-[#94A3B8]">views</span></span>
              <span className="flex items-center gap-1.5" title="Clicks on this advertiser's placement"><MousePointerClick size={13} className="text-[#94A3B8]" />{ad.click_count || 0} <span className="text-[#94A3B8]">clicks</span></span>
            </div>
            <Switch data-testid={`advertiser-enabled-${ad.id}`} checked={ad.enabled !== false} onCheckedChange={(checked) => toggleEnabled(ad, checked)} />
            <button data-testid={`advertiser-edit-${ad.id}`} onClick={() => setEditing(ad)} title="Edit" className="p-2 text-[#475467] hover:text-[#0C2347]"><Pencil size={16} /></button>
            <button data-testid={`advertiser-delete-${ad.id}`} onClick={() => remove(ad)} title="Remove" className="p-2 text-red-600 hover:text-red-700"><Trash2 size={16} /></button>
          </div>
        ))}
      </section>

      {(adding || editing) && (
        <AdvertiserFormDialog advertiser={editing} onClose={() => { setAdding(false); setEditing(null); }} onSaved={() => { setAdding(false); setEditing(null); reload(); }} />
      )}
    </div>
  );
}

function AdvertiserFormDialog({ advertiser, onClose, onSaved }) {
  const [form, setForm] = useState(advertiser ? { ...EMPTY_FORM, ...advertiser } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.business_name.trim()) { toast.error("Enter a business name"); return; }
    setSaving(true);
    const payload = { ...form, contact_email: form.contact_email || null, active_from: form.active_from || null, active_until: form.active_until || null };
    try {
      if (advertiser) await api.patch(`/admin/advertisers/${advertiser.id}`, payload);
      else await api.post("/admin/advertisers", payload);
      toast.success(advertiser ? "Advertiser updated" : "Advertiser added");
      onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const field = (key, label, props = {}) => (
    <div>
      <Label className="text-[10px] uppercase font-mono tracking-[0.1em] text-[#475467]">{label}</Label>
      <Input data-testid={`advertiser-form-${key}`} value={form[key] || ""} onChange={(e) => set(key)(e.target.value)} className="mt-1 rounded-xl border-[#E4E9F0]" {...props} />
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="advertiser-form-dialog" className="rounded-xl max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl font-normal text-[#101828]">{advertiser ? "Edit advertiser" : "Add advertiser"}</DialogTitle>
          <DialogDescription>Shown as a sponsored placement on any property website you assign it to.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {field("business_name", "Business name")}
          <div>
            <Label className="text-[10px] uppercase font-mono tracking-[0.1em] text-[#475467]">Category</Label>
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger data-testid="advertiser-form-category" className="mt-1 rounded-xl border-[#E4E9F0]"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("headline", "Headline", { placeholder: "Low rates, fast pre-approval", maxLength: 140 })}
          {field("button_label", "Button label", { placeholder: "Visit Website" })}
          {field("destination_url", "Destination URL", { placeholder: "https://… (leave blank to collect inquiries instead)" })}
          {field("service_area", "Service area", { placeholder: "Greater Austin area" })}
          {field("logo_url", "Logo image URL")}
          {field("image_url", "Banner image URL")}
          {field("contact_email", "Contact email", { type: "email" })}
          {field("contact_phone", "Contact phone")}
          {field("active_from", "Active from", { type: "date" })}
          {field("active_until", "Active until", { type: "date" })}
        </div>
        <div>
          <Label className="text-[10px] uppercase font-mono tracking-[0.1em] text-[#475467]">Description</Label>
          <Textarea data-testid="advertiser-form-description" rows={3} maxLength={600} value={form.description} onChange={(e) => set("description")(e.target.value)} className="mt-1 rounded-xl border-[#E4E9F0]" />
        </div>
        <div>
          <Label className="text-[10px] uppercase font-mono tracking-[0.1em] text-[#475467]">Disclosure text (optional)</Label>
          <Input data-testid="advertiser-form-disclosure" placeholder="e.g. Paid advertisement, not an endorsement" value={form.disclosure_text} onChange={(e) => set("disclosure_text")(e.target.value)} className="mt-1 rounded-xl border-[#E4E9F0]" />
        </div>
        <div className="flex items-center justify-between rounded-xl border border-[#E4E9F0] bg-[#F7F9FC] px-4 py-3">
          <div><div className="text-sm font-medium text-[#101828]">Enabled</div><div className="text-xs text-[#475467]">Disabled advertisers are hidden everywhere, even if assigned.</div></div>
          <Switch data-testid="advertiser-form-enabled" checked={form.enabled} onCheckedChange={set("enabled")} />
        </div>
        <DialogFooter>
          <Button data-testid="advertiser-form-cancel" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="advertiser-form-save" disabled={saving} onClick={save} className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white">{saving ? "Saving…" : "Save advertiser"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
